
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DetailView } from '@/components/ui/detail-view';
import { Employee, Payslip } from '@/types';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { EditEmployeeForm } from '@/components/employees/EditEmployeeForm';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PayrollProcessor } from '@/components/payroll/PayrollProcessor';
import { PayslipList } from '@/components/payslips/PayslipList';
import { PayslipDetails } from '@/components/payslips/PayslipDetails';
import { useAppDataContext } from '@/contexts/AppDataContext';

const EmployeeDetail = () => {
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [openEditForm, setOpenEditForm] = useState(false);
  const { toast } = useToast();
  const { data, refresh } = useAppDataContext();
  
  // State for payslips
  const [employeePayslips, setEmployeePayslips] = useState<Payslip[]>([]);
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [payslipDetailOpen, setPayslipDetailOpen] = useState(false);

  useEffect(() => {
    const fetchEmployee = async () => {
      if (!id || typeof id !== 'string') {
        return;
      }
      try {
        const employeeData = await apiService.getEmployee(id);
        const payload = employeeData as { data?: unknown };
        const resolved = Array.isArray(payload?.data)
          ? (payload.data[0] as Employee)
          : (payload?.data ?? employeeData) as Employee;
        setEmployee(resolved ?? null);
      } catch (error) {
        console.error('Error fetching employee:', error);
        toast({
          title: 'Error',
          description: 'Failed to load employee details',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id, toast]);
  
  // Filter payslips for this employee
  useEffect(() => {
    if (employee && data.payslips) {
      const filteredPayslips = data.payslips.filter(p => p.employeeId === employee.id);
      setEmployeePayslips(filteredPayslips);
      
      // Select the first payslip if available and none is selected
      if (filteredPayslips.length > 0 && !selectedPayslip) {
        setSelectedPayslip(filteredPayslips[0]);
      }
    }
  }, [employee, data.payslips, selectedPayslip]);

  const handleEditEmployee = () => {
    setOpenEditForm(true);
  };

  const handleUpdateEmployee = async (updatedEmployee: Partial<Employee>) => {
    if (!employee?.id) {
      return;
    }
    try {
      await apiService.updateEmployee(employee.id, updatedEmployee);
      toast({
        title: 'Success',
        description: 'Employee updated successfully',
      });
      
      // Refresh employee data
      const updatedEmployeeData = await apiService.getEmployee(employee.id);
      const payload = updatedEmployeeData as { data?: unknown };
      const resolved = Array.isArray(payload?.data)
        ? (payload.data[0] as Employee)
        : (payload?.data ?? updatedEmployeeData) as Employee;
      setEmployee(resolved ?? null);
      
      setOpenEditForm(false);
    } catch (error) {
      console.error('Error updating employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to update employee',
        variant: 'destructive',
      });
    }
  };
  
  const handleViewPayslip = (payslip: Payslip) => {
    setSelectedPayslip(payslip);
    setPayslipDetailOpen(true);
  };
  
  const handlePayrollProcessed = async () => {
    await refresh.payslips();
    toast({
      title: 'Payroll Processed',
      description: 'Payroll has been processed successfully for this employee',
    });
  };
  
  const handleCreatePayslip = async (employeeId: string, payslipData: any) => {
    try {
      // Create a new payslip with proper ID field
      const newPayslip: Payslip = {
        id: `payslip-${Date.now()}`, // Generate a unique ID
        employeeId: employeeId,
        period: payslipData.payPeriod,
        payPeriod: formatPayPeriod(payslipData.payPeriod),
        issueDate: payslipData.generatedDate || new Date().toISOString().split('T')[0],
        basicSalary: payslipData.basicSalary,
        totalAllowances: payslipData.totalAllowances,
        totalDeductions: payslipData.totalDeductions,
        netSalary: payslipData.netSalary,
        status: 'draft'
      };
      
      await apiService.createPayslip(newPayslip);
      
      // Refresh payslips
      await refresh.payslips();
      
      toast({
        title: "Payslip created",
        description: "Payslip has been created successfully.",
      });
      
      return true;
    } catch (error) {
      console.error('Error creating payslip:', error);
      toast({
        title: "Error",
        description: "Failed to create payslip. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const formatPayPeriod = (period: string) => {
    const date = new Date(period + "-01");
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      </DashboardLayout>
    );
  }

  if (!employee) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Card>
            <CardContent className="p-6">
              <CardTitle className="text-lg font-medium">Employee Not Found</CardTitle>
              <p className="text-sm text-gray-500">Could not find employee with ID: {id}</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Employee Details</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/employees')}>
              Back to Employees
            </Button>
            <Button onClick={handleEditEmployee}>Edit Employee</Button>
          </div>
        </div>

        <Tabs defaultValue="details">
          <TabsList className="mb-6">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="payroll">Payroll</TabsTrigger>
            <TabsTrigger value="payslips">Payslips</TabsTrigger>
          </TabsList>
        
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>{employee?.firstName} {employee?.lastName}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Employee ID</p>
                    <p className="text-gray-700">{employee?.employeeId}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="text-gray-700">{employee?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="text-gray-700">{employee?.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Position</p>
                    <p className="text-gray-700">{employee?.position}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Department</p>
                    <p className="text-gray-700">{employee?.department}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="text-gray-700">{employee?.status}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Basic Salary</p>
                    <p className="text-gray-700">
                      {employee?.basicSalary 
                        ? `ZMW ${employee.basicSalary.toFixed(2)}` 
                        : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Hire Date</p>
                    <p className="text-gray-700">{employee?.joinDate || 'Not set'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="payroll">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle>Process Payroll</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PayrollProcessor onProcessed={handlePayrollProcessed} />
                  </CardContent>
                </Card>
              </div>
              
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Payroll History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {employeePayslips.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No payroll history available for this employee
                      </div>
                    ) : (
                      <PayslipList
                        payslips={employeePayslips}
                        employees={[employee]}
                        onViewPayslip={handleViewPayslip}
                        selectedPayslipId={selectedPayslip?.id}
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="payslips">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle>Employee Payslips</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {employeePayslips.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No payslips available for this employee
                      </div>
                    ) : (
                      <PayslipList
                        payslips={employeePayslips}
                        employees={[employee]}
                        onViewPayslip={handleViewPayslip}
                        selectedPayslipId={selectedPayslip?.id}
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
              
              <div className="lg:col-span-2">
                {selectedPayslip ? (
                  <PayslipDetails
                    payslip={selectedPayslip}
                    employeeName={`${employee.firstName} ${employee.lastName}`}
                    onDownload={() => {
                      toast({
                        title: "Download initiated",
                        description: "Payslip download has started.",
                      });
                    }}
                  />
                ) : (
                  <Card className="h-[calc(100vh-320px)] flex items-center justify-center">
                    <div className="text-center p-6">
                      <CardTitle className="mb-4">No Payslip Selected</CardTitle>
                      <p className="text-gray-500">
                        Select a payslip from the list to view details
                      </p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <EditEmployeeForm
          key={employee.id}
          isOpen={openEditForm}
          onClose={() => setOpenEditForm(false)}
          employee={employee}
          onSubmit={handleUpdateEmployee}
        />
        
        {selectedPayslip && (
          <DetailView
            isOpen={payslipDetailOpen}
            onClose={() => setPayslipDetailOpen(false)}
            title={`Payslip - ${employee.firstName} ${employee.lastName} - ${selectedPayslip.payPeriod}`}
            size="xl"
          >
            <PayslipDetails 
              payslip={selectedPayslip}
              employeeName={`${employee.firstName} ${employee.lastName}`}
              onDownload={() => {
                toast({
                  title: "Download initiated",
                  description: "Payslip download has started.",
                });
              }}
            />
          </DetailView>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmployeeDetail;
